import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { getDomainConfig } from './domains';
import { useCapabilities } from '../../../store/PermissionSets';
import { TargetedDenyCapability } from '../../../types';

export interface TargetedDeniesEditorProps {
  value: string[];
  onChange: (denies: string[]) => void;
  readOnly?: boolean;
}

const groupLabel = (capability: TargetedDenyCapability): string =>
  capability.domain ? (getDomainConfig(capability.domain)?.label ?? capability.domain) : 'General';

const HELPER_TEXT =
  "Deny specific cross-cutting writes (e.g. circuit disconnect) regardless of a domain's write access.";

export const TargetedDeniesEditor = ({ value, onChange, readOnly }: TargetedDeniesEditorProps) => {
  const { capabilities, loading, error } = useCapabilities();

  const orphans: TargetedDenyCapability[] = value
    .filter((key) => !capabilities.some((capability) => capability.key === key))
    .map((key) => ({ key, domain: null, label: key }));
  const options = [...capabilities, ...orphans];
  const selected = options.filter((option) => value.includes(option.key));

  return (
    <Autocomplete
      multiple
      readOnly={readOnly}
      loading={loading}
      options={options}
      groupBy={groupLabel}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, chosen) => option.key === chosen.key}
      value={selected}
      onChange={(_event, chosen) => onChange(chosen.map((capability) => capability.key))}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Action Restrictions"
          error={Boolean(error)}
          helperText={readOnly ? undefined : (error ?? HELPER_TEXT)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};
